/*
 * Jira Ticket:
 * Created Date: Tue, 17th Mar 2020, 17:57:49 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React, { forwardRef, useImperativeHandle, useState } from 'react';
import GoogleMapReact from 'google-map-react';

const MapComponent = forwardRef((props, ref) => {
  const {
    initialLocation = null,
    defaultCenter = { lat: 7.0, lng: 7.0 },
    mapZoom,
    defaultMapZoom,
    updateFormValues,
    disableDefaultUI,
    setMapLoaded,
  } = props;
  const [map, setMap] = useState(null);
  const [geocoder, setGeocoder] = useState(null);
  const [siteMarker, setSiteMarker] = useState(null);

  const handleApiLoaded = ({ map, maps, initialLocation }) => {
    // always create a marker on load
    const marker = new maps.Marker({ position: null, map });
    if (initialLocation) {
      // for the edit view
      marker.setPosition(initialLocation);
    }
    return setSiteMarker(marker);
  };

  window.gm_authFailure = () => {
    // detect an error and allow lat/lng fields to be editable
    setMapLoaded(false);
  };

  const getClickCoords = ({ lat, lng }) => {
    if (disableDefaultUI) {
      // prevent user without correct role from clicking on map
      return;
    }
    const position = { lat, lng };
    siteMarker.setPosition(position);
    map.setCenter(position);
    return updateFormValues(position);
  };

  useImperativeHandle(ref, () => ({
    performSearch(geocodingParams) {
      return new Promise(function(resolve, reject) {
        geocoder.geocode(geocodingParams, (results, status) => {
          if (status === 'OK') {
            const position = {
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng(),
            };
            siteMarker.setPosition(position);
            map.setZoom(mapZoom);
            map.setCenter(position);
            resolve(position);
          } else if (status === 'ZERO_RESULTS') {
            reject({
              message: 'error.maps.addressDoesNotExist',
              messageArgs: { ...geocodingParams },
            });
          } else {
            reject({
              message: 'error.maps.geocoderFailure',
              messageArgs: status,
            });
          }
        });
      });
    },
  }));

  return (
    <GoogleMapReact
      bootstrapURLKeys={{
        key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
      }}
      yesIWantToUseGoogleMapApiInternals
      onGoogleApiLoaded={({ map, maps }) => {
        setMapLoaded(true);
        setMap(map);
        setGeocoder(new maps.Geocoder());
        return handleApiLoaded({ map, maps, initialLocation, mapZoom });
      }}
      onClick={getClickCoords}
      // Center map on the user's location if not null, otherwise use default props.
      center={initialLocation || defaultCenter}
      onError
      defaultZoom={defaultMapZoom || mapZoom}
      // prevent user without correct role from interacting with map
      options={{
        gestureHandling: disableDefaultUI ? 'none' : 'auto',
        disableDefaultUI,
      }}
    />
  );
});

export default MapComponent;
